/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  ControlledSelect,
  DynamicFormInput,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../../components";
import { withProtectedRoute } from "../../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useMarketPricing } from "../../../store/useMarketPricing";
import { useStatiegeldProducts } from "../../../store/useStatiegeldProducts";
import {
  REPEATABLE_CHALLENGE_TYPES,
  usePartnerChallenges,
} from "../../../store/usePartnerChallenges";
import { usePartners } from "../../../store/usePartners";
import { useUploadImage } from "../../../store/useUploadImage";
import {
  BANNER_IMAGE_HINT,
  CARD_IMAGE_HINT,
  ImageUploadHint,
} from "../../../components/form/ImageUploadHint";
import { useDropLocations } from "../../../store/useDropLocations";
import { storeLabel } from "../../../utils/storeLabel";
import { useAlert } from "../../../store/useAlert";
import { useVouchers } from "../../../store/useVouchers";
import { CompletedChallengesTable } from "./CompletedChallengesTable";
import { ChallengeCompletionsTable } from "./ChallengeCompletionsTable";

type Inputs = {
  en_title: string;
  nl_title: string;
  en_description: string;
  nl_description: string;
  partnerId: string;
  startsAt?: string;
  endsAt?: string;
  rewardType: "VOUCHER" | "CASH" | "DROPPIES";
  rewardValue?: number;
  type:
    | "FIRST_DROP"
    | "DROP_COUNT"
    | "DROP_VALUE"
    | "ITEM_TYPE"
    | "ITEM_TYPE_COUNT"
    | "ITEM_TYPE_DROP_COUNT"
    | "ITEM_TYPE_VALUE"
    | "TOMRA_ITEM_TYPE"
    | "TOMRA_ITEM_TYPE_COUNT"
    | "TOMRA_ITEM_TYPE_DROP_COUNT"
    | "TOMRA_ITEM_TYPE_VALUE"
    | "DONATION"
    | "INVITE"
    | "STREAKS"
    | "POSTAL_CODE"
    | "COMPLETE_PROFILE"
    | "PARCEL_COUNT";
  minDrops?: number;
  minValue?: number;
  itemType?: string;
  tomraItemType?: string;
  streakType?: "DAILY" | "WEEKLY" | "MONTHLY";
  streakCount?: number;
  donationCount?: number;
  minDonationAmount?: number;
  parcelDirection?: "PICKUP" | "DROPOFF" | "EITHER";
  minParcelCount?: number;
  showParticipants?: boolean;
};

function EditPartnerChallengeScreen() {
  /** Hooks */
  const params = useParams();
  const navigate = useNavigate();
  const {
    updatePartnerChallenge,
    getOnePartnerChallenge,
    onePartnerChallenge,
    updatedPartnerChallenge,
    clearUpdatedPartnerChallenge,
    allCompletedChallenges,
    getAllCompletedChallenges,
    clearAllCompletedChallenges,
    hasNextCompletedChallenges,
    challengeCompletions,
    challengeCompletionsTotal,
    hasNextChallengeCompletions,
    getChallengeCompletions,
    clearChallengeCompletions,
  } = usePartnerChallenges();
  const { getAllPartners, allPartners, getCouriers, couriers } = usePartners();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const { getStatiegeldProducts, statiegeldProducts } = useStatiegeldProducts();
  const {
    uploadImage,
    clearUploadedImage,
    uploadedImage,
    setUploadedImage,
    uploadFile,
  } = useUploadImage();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  const { dropLocations, getDropLocations } = useDropLocations();
  const { setAlert } = useAlert();
  const {
    activeVouchers,
    getPartnerActiveVouchers,
    clearPartnerActiveVouchers,
  } = useVouchers();
  /** State */
  const [page, setPage] = useState<number>(1);
  const [completionsPage, setCompletionsPage] = useState<number>(1);
  const [pendingOnly, setPendingOnly] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);
  const [cardImage, setCardImage] = useState<File | null>(null);
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
  const [cardUploading, setCardUploading] = useState<boolean>(false);
  const [cities, setCities] = useState<
    {
      id: number | string;
      value: string;
    }[]
  >([]);
  // Where the challenge runs: every store in the chosen cities, or only the
  // chosen stores. The backend ignores cities when stores are set.
  const [targetType, setTargetType] = useState<"cities" | "locations">(
    "cities"
  );
  const [stores, setStores] = useState<
    {
      id: number | string;
      value: string;
    }[]
  >([]);

  /** Callbacks */
  const goBack = () => navigate(-1);
  const onSubmit = (data: Inputs) => {
    if (cardUploading) {
      setAlert({
        isOpen: true,
        severity: "error",
        message: "Wait for the card image to finish uploading.",
      });
      return;
    }
    if (targetType === "locations" && stores.length === 0) {
      setAlert({
        isOpen: true,
        severity: "error",
        message: "Pick at least one store, or switch to Cities.",
      });
      return;
    }
    const body: any = {
      en_title: data.en_title,
      nl_title: data.nl_title,
      en_description: data.en_description,
      nl_description: data.nl_description,
      conditions: {
        type: data.type,
      },
      rewardType: data.type === "PARCEL_COUNT" ? "VOUCHER" : data.rewardType,
    };
    if (
      data.type !== "PARCEL_COUNT" &&
      data.rewardType !== "VOUCHER" &&
      data.rewardValue
    ) {
      body.rewardValue = data.rewardValue;
    }
    if (uploadedImage) {
      body.backgroundImage = uploadedImage;
    }
    // Always sent, so removing the card image in the form removes it here too.
    body.cardImage = cardImageUrl ?? null;
    if (data.type === "ITEM_TYPE") {
      body.conditions.itemType = data.itemType;
    }
    if (
      data.type === "ITEM_TYPE_DROP_COUNT" ||
      data.type === "ITEM_TYPE_COUNT"
    ) {
      body.conditions.itemType = data.itemType;
      if (data.minDrops) {
        body.conditions.minDrops = data.minDrops;
      }
    }
    if (data.type === "ITEM_TYPE_VALUE") {
      body.conditions.itemType = data.itemType;
      if (data.minValue) {
        body.conditions.minValue = Number(data.minValue * 100) as number;
      }
    }
    if (data.type === "DROP_COUNT") {
      if (data.minDrops) {
        body.conditions.minDrops = data.minDrops;
      }
    }
    if (data.type === "DROP_VALUE") {
      if (data.minValue) {
        body.conditions.minValue = Number(data.minValue * 100) as number;
      }
    }
    if (data?.type === "DONATION") {
      body.conditions.donationCount = data.donationCount;
      // Sent even while the input is read-only: the update replaces the
      // conditions whole, so leaving it out would remove the minimum, which
      // the server refuses on a repeating challenge.
      if (data.minDonationAmount) {
        body.conditions.minDonationAmount = Number(
          data.minDonationAmount * 100
        ) as number;
      }
    }
    if (data.type === "TOMRA_ITEM_TYPE") {
      body.conditions.tomraItemType = data.tomraItemType;
    }
    if (
      data.type === "TOMRA_ITEM_TYPE_DROP_COUNT" ||
      data.type === "TOMRA_ITEM_TYPE_COUNT"
    ) {
      body.conditions.tomraItemType = data.tomraItemType;
      if (data.minDrops) {
        body.conditions.minDrops = data.minDrops;
      }
    }
    if (data.type === "TOMRA_ITEM_TYPE_VALUE") {
      body.conditions.tomraItemType = data.tomraItemType;
      if (data.minValue) {
        body.conditions.minValue = Number(data.minValue * 100) as number;
      }
    }
    if (data.type === "STREAKS") {
      body.conditions.streakType = data.streakType;
      body.conditions.streakCount = data.streakCount;
    }
    if (data.type === "PARCEL_COUNT") {
      // Conditions are read-only after creation here, as they are for every
      // other type — progress is already counted against them. The carriers
      // are echoed back from the saved challenge so the update does not blank
      // them out; the backend rejects an empty list.
      body.conditions.carrierPartnerIds = (
        onePartnerChallenge?.conditions?.carrierPartnerIds ?? []
      ).map((c: any) => String(c?._id ?? c));
      body.conditions.parcelDirection =
        onePartnerChallenge?.conditions?.parcelDirection ?? "EITHER";
      body.conditions.minParcelCount =
        onePartnerChallenge?.conditions?.minParcelCount;
    }
    if (targetType === "locations") {
      body.locationIds = stores.map((input) => String(input?.id));
    } else {
      if (cities?.length) {
        body.cities = cities?.map((input) => input?.id) ?? [];
      }
      // Switching a store challenge back to cities has to clear the stores
      // explicitly; leaving the field out keeps them.
      if (onePartnerChallenge?.locationIds?.length) {
        body.locationIds = null;
      }
    }
    body.showParticipants = data.showParticipants || false;
    // Sent even when cleared, so an operator can un-schedule a challenge and
    // have it go live — an `if (data.startsAt)` guard would make the field
    // one-way (settable, never removable).
    body.startsAt = data.startsAt || null;
    if (data.endsAt) {
      body.endsAt = data.endsAt;
    }
    updatePartnerChallenge(params?.id as string, body);
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onCardImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    // Cleared so that picking the same file again still fires a change.
    e.target.value = "";
    if (file) {
      setCardImage(file);
    }
  };

  /** Effects */
  useEffect(() => {
    getAllMarketPrices();
    getStatiegeldProducts();
    getAllPartners(1, 1000);
    getCouriers();
    getDropLocations();
    return () => {
      setImage(null);
      clearUploadedImage();
      clearPartnerActiveVouchers();
      clearAllCompletedChallenges();
      clearChallengeCompletions();
    };
  }, []);

  useEffect(() => {
    if (params?.id) {
      getOnePartnerChallenge(params.id);
      getAllCompletedChallenges(1, 10, params?.id);
    }
  }, [params?.id]);

  // The completion ledger is the only place a recurring challenge's rewards
  // are recorded, so it is fetched once the challenge is known to be one.
  // Re-fetches from page 1 when the filter flips, because a pending-only view
  // and the full log are different lists, not a subset of the same one.
  useEffect(() => {
    if (params?.id && onePartnerChallenge?.allowMultipleCompletions) {
      setCompletionsPage(1);
      getChallengeCompletions(
        params.id,
        1,
        20,
        pendingOnly ? "PENDING_STOCK" : undefined
      );
    }
  }, [params?.id, onePartnerChallenge?.allowMultipleCompletions, pendingOnly]);

  useEffect(() => {
    if (onePartnerChallenge) {
      reset({
        en_title: onePartnerChallenge.en_title,
        nl_title: onePartnerChallenge.nl_title,
        en_description: onePartnerChallenge.en_description,
        nl_description: onePartnerChallenge.nl_description,
        type: onePartnerChallenge.conditions.type,
        partnerId: onePartnerChallenge?.partnerId,
        rewardType: onePartnerChallenge.rewardType || "VOUCHER",
      });
      setCities(
        onePartnerChallenge?.cities?.map((ct) => ({ id: ct, value: ct })) ?? []
      );
      setTargetType(
        onePartnerChallenge?.locationIds?.length ? "locations" : "cities"
      );
      setUploadedImage(onePartnerChallenge.backgroundImage ?? null);
      setCardImage(null);
      setCardImageUrl(onePartnerChallenge.cardImage ?? null);
      if (onePartnerChallenge.conditions.minDrops) {
        setValue("minDrops", onePartnerChallenge.conditions.minDrops);
      }
      if (onePartnerChallenge.conditions.minValue) {
        setValue("minValue", onePartnerChallenge.conditions.minValue / 100);
      }
      if (onePartnerChallenge.conditions.itemType) {
        // @ts-ignore
        setValue("itemType", onePartnerChallenge.conditions.itemType);
      }
      if (onePartnerChallenge.conditions.tomraItemType) {
        setValue(
          "tomraItemType",
          // @ts-ignore
          onePartnerChallenge.conditions.tomraItemType
        );
      }
      if (onePartnerChallenge.conditions.streakType) {
        setValue("streakType", onePartnerChallenge.conditions.streakType);
      }
      if (onePartnerChallenge.conditions.streakCount) {
        setValue("streakCount", onePartnerChallenge.conditions.streakCount);
      }
      if (onePartnerChallenge.conditions.donationCount) {
        setValue("donationCount", onePartnerChallenge.conditions.donationCount);
      }
      if (onePartnerChallenge.conditions.minDonationAmount) {
        setValue(
          "minDonationAmount",
          onePartnerChallenge.conditions.minDonationAmount / 100
        );
      }
      if (onePartnerChallenge.conditions.parcelDirection) {
        setValue(
          "parcelDirection",
          onePartnerChallenge.conditions.parcelDirection
        );
      }
      if (onePartnerChallenge.conditions.minParcelCount) {
        setValue(
          "minParcelCount",
          onePartnerChallenge.conditions.minParcelCount
        );
      }
      if (onePartnerChallenge.rewardValue) {
        setValue("rewardValue", onePartnerChallenge.rewardValue);
      }
      setValue("showParticipants", (onePartnerChallenge as any).showParticipants || false);
      if ((onePartnerChallenge as any).startsAt) {
        setValue(
          "startsAt",
          new Date((onePartnerChallenge as any).startsAt)
            .toISOString()
            .split("T")[0],
        );
      }
      if ((onePartnerChallenge as any).endsAt) {
        setValue(
          "endsAt",
          new Date((onePartnerChallenge as any).endsAt).toISOString().split("T")[0],
        );
      }
    }
  }, [onePartnerChallenge]);

  useEffect(() => {
    if (!cardImage) return;
    let current = true;
    setCardUploading(true);
    uploadFile(cardImage).then((url) => {
      if (!current) return;
      setCardUploading(false);
      if (url) {
        setCardImageUrl(url);
        return;
      }
      // Keep the preview in step with what Save sends: a failed upload
      // leaves the previous card image (or none) in place.
      setCardImage(null);
      setAlert({
        isOpen: true,
        severity: "error",
        message: "The card image could not be uploaded. Please try again.",
      });
    });
    return () => {
      current = false;
      setCardUploading(false);
    };
  }, [cardImage]);

  useEffect(() => {
    if (updatedPartnerChallenge) {
      navigate(-1);
      clearUpdatedPartnerChallenge();
      setImage(null);
      clearUploadedImage();
    }
  }, [updatedPartnerChallenge]);

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  useEffect(() => {
    if (watch("partnerId")) {
      getPartnerActiveVouchers(watch("partnerId"));
    }
  }, [watch("partnerId")]);

  const selectedPartner = useMemo(
    () => allPartners?.find((p) => p?._id === watch("partnerId")) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allPartners, watch("partnerId")],
  );

  // Carrier ids come back either populated or raw depending on the endpoint,
  // so resolve through the courier list and fall back to the id.
  // Locked to a voucher, same as on create. A legacy row saved with a money
  // reward is shown as the problem it is rather than being quietly corrected
  // on save — someone has to know it was misconfigured.
  const isParcelChallenge = watch("type") === "PARCEL_COUNT";
  const hasInvalidLegacyReward =
    isParcelChallenge &&
    Boolean(onePartnerChallenge) &&
    onePartnerChallenge?.rewardType !== "VOUCHER";
  useEffect(() => {
    if (isParcelChallenge && watch("rewardType") !== "VOUCHER") {
      setValue("rewardType", "VOUCHER");
    }
  }, [isParcelChallenge]);

  const isRepeatableType = REPEATABLE_CHALLENGE_TYPES.includes(watch("type"));
  const isRepeating = Boolean(onePartnerChallenge?.allowMultipleCompletions);

  const selectedCarrierNames = useMemo(() => {
    const ids = (onePartnerChallenge?.conditions?.carrierPartnerIds ??
      []) as any[];
    return ids.map((c) => {
      const id = String(c?._id ?? c);
      return (
        c?.name ?? couriers?.find((p) => p?._id === id)?.name ?? id
      );
    });
  }, [onePartnerChallenge, couriers]);

  const citiesToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    let locationsToSelect: { value: string; label: string }[] = [];
    for (let location of dropLocations) {
      if (locationsToSelect?.some((loc) => loc?.value === location?.city))
        continue;
      locationsToSelect.push({
        label: location?.city as string,
        value: location?.city as string,
      });
    }
    return locationsToSelect;
  }, [dropLocations]);

  const storesToSelect = useMemo(() => {
    const chosen = stores.map((store) => String(store.id));
    return (dropLocations ?? [])
      .filter((location) => !chosen.includes(String(location?._id)))
      .map((location) => ({
        label: storeLabel(location),
        value: location?._id as string,
      }));
  }, [dropLocations, stores]);

  useEffect(() => {
    const ids = onePartnerChallenge?.locationIds ?? [];
    setStores(
      ids.map((id) => {
        const location = dropLocations?.find(
          (loc) => String(loc?._id) === String(id)
        );
        return {
          id: String(id),
          value: location ? storeLabel(location) : String(id),
        };
      })
    );
  }, [onePartnerChallenge, dropLocations]);

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Edit Partner Challenge
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border-gray-border drop-shadow-none rounded-xl mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Partner Challenge
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            {/* Name */}
            <ControlledInput
              name="en_title"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.en_title)}
              label="Title (English)"
              type="text"
              placeholder="Enter title here in English"
            />
            <ControlledInput
              name="nl_title"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.nl_title)}
              label="Title (Dutch)"
              type="text"
              placeholder="Enter title here in Dutch"
            />
            {/* Description */}
            <ControlledInput
              name="en_description"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.en_description)}
              label="Description (English)"
              type="text"
              placeholder="Enter description here in English"
            />
            <ControlledInput
              name="nl_description"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.nl_description)}
              label="Description (Dutch)"
              type="text"
              placeholder="Enter description here in Dutch"
            />
            <ControlledSelect
              name="partnerId"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.partnerId)}
              label="Partner"
              // The type cannot change here, and a courier partner only ever
              // backs a parcel challenge, so couriers are offered for parcel
              // challenges only.
              options={allPartners
                ?.filter((partner) => isParcelChallenge || !partner?.isCourier)
                .map((partner) => ({
                  _id: partner?._id as string,
                  title: partner?.name as string,
                }))}
              placeholder="Pick Partner here"
              inputClassName="w-full"
              className="w-full"
            />
            {/* Never for a carrier: a carrier is out of the app's partner
                list by rule, not by mistake. */}
            {selectedPartner &&
              selectedPartner.showInApp === false &&
              !selectedPartner.isCourier && (
                <p className="font-satoshi text-sm text-red-600 mb-4">
                  This partner is not visible in the app (Show in App is
                  disabled).
                </p>
              )}
            {typeof activeVouchers === "number" &&
            watch("rewardType") === "VOUCHER" ? (
              <div className="mb-6">
                <p
                  className={`font-satoshi text-sm ${
                    activeVouchers === 0 ? "text-red-600" : "text-dark"
                  }`}
                >
                  {activeVouchers} active vouchers from this Partner
                </p>
                {activeVouchers === 0 ? (
                  <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2">
                    <p className="font-satoshi text-sm font-medium text-red-700">
                      No voucher assigned
                    </p>
                    <p className="font-satoshi text-sm text-red-700">
                      A voucher challenge stays hidden in the app until this
                      partner has a voucher. You can save it now and add the
                      voucher under Challenges, Vouchers.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
            <ControlledSelect
              name="rewardType"
              control={control}
              rules={{ required: true }}
              disabled={isParcelChallenge}
              error={Boolean(errors?.rewardType)}
              label="Reward Type"
              options={[
                { _id: "VOUCHER", title: "Voucher" },
                { _id: "CASH", title: "Cash" },
              ]}
              placeholder="Pick reward type here"
            />
            {isParcelChallenge ? (
              <p className="font-satoshi text-sm text-dark -mt-2 mb-4">
                Parcel challenges always reward a voucher.
              </p>
            ) : null}
            {hasInvalidLegacyReward ? (
              <p className="font-satoshi text-sm text-red-600 mb-4">
                This parcel challenge is saved with a{" "}
                {onePartnerChallenge?.rewardType} reward, which is no longer
                allowed. Saving will switch it to a voucher — make sure the
                reward partner has voucher stock first.
              </p>
            ) : null}
            {!isParcelChallenge &&
              watch("rewardType") === "CASH" && (
              <>
                <ControlledInput
                  name="rewardValue"
                  rules={{ required: true }}
                  control={control}
                  error={Boolean(errors?.rewardValue)}
                  label="Reward Value"
                  type="number"
                  placeholder="Enter reward value here"
                />
                {watch("rewardType") === "CASH" ? (
                  <p
                    className={`font-satoshi text-sm  mb-6 ${
                      watch("rewardValue") === 0 ? "text-red-600" : "text-dark"
                    }`}
                  >
                    {new Intl.NumberFormat("nl-NL", {
                      style: "currency",
                      currency: "EUR",
                    }).format(Number(watch("rewardValue") ?? 0))}{" "}
                    will be added to the User after completing the Challenge
                  </p>
                ) : null}
              </>
            )}
            {/* Background image upload */}
            <FormUpload
              label="Challenge background image (banner)"
              accept=".png, .jpg, .jpeg"
              formatsHint="PNG or JPG"
              sizeHint="Best: 1200 x 500 px (wide)"
              onChange={onImageChange}
              value={image}
              imageUrl={uploadedImage ?? ""}
              error={false}
            />
            <ImageUploadHint rows={BANNER_IMAGE_HINT} />
            {/* Card image upload */}
            <FormUpload
              label="Card image (square)"
              accept=".png, .jpg, .jpeg"
              formatsHint="PNG or JPG"
              sizeHint="Best: 800 x 800 px (square)"
              onChange={onCardImageChange}
              value={cardImage}
              imageUrl={cardImageUrl ?? ""}
              error={false}
              onClear={() => {
                setCardImage(null);
                setCardImageUrl(null);
              }}
            />
            <ImageUploadHint rows={CARD_IMAGE_HINT} />
            {/* Challenge Type */}
            <ControlledSelect
              name="type"
              disabled
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.type)}
              label="Challenge type"
              options={types}
              placeholder="Pick Challenge type here"
            />
            {/* Min Items */}
            {watch("type") === "ITEM_TYPE_COUNT" ||
            watch("type") === "TOMRA_ITEM_TYPE_COUNT" ? (
              <ControlledInput
                disabled
                name="minDrops"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minDrops)}
                label="Min items"
                type="number"
                placeholder="Enter min items here"
              />
            ) : null}
            {/* Min Drops */}
            {watch("type") === "DROP_COUNT" ||
            watch("type") === "ITEM_TYPE_DROP_COUNT" ||
            watch("type") === "TOMRA_ITEM_TYPE_DROP_COUNT" ? (
              <ControlledInput
                disabled
                name="minDrops"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minDrops)}
                label="Min Drops"
                type="number"
                placeholder="Enter min drops here"
              />
            ) : null}
            {/* Min value */}
            {watch("type") === "DROP_VALUE" ||
            watch("type") === "ITEM_TYPE_VALUE" ||
            watch("type") === "TOMRA_ITEM_TYPE_VALUE" ? (
              <ControlledInput
                disabled
                name="minValue"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minValue)}
                label="Min Value"
                type="number"
                placeholder="Enter min value of drop here"
              />
            ) : null}
            {watch("type") === "ITEM_TYPE" ||
            watch("type") === "ITEM_TYPE_COUNT" ||
            watch("type") === "ITEM_TYPE_VALUE" ? (
              <ControlledSelect
                disabled
                name="itemType"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.itemType)}
                label="Composition type"
                options={marketPrices?.map((price) => ({
                  _id: price?._id as string,
                  title: price?.name as string,
                }))}
                placeholder="Pick item type here"
              />
            ) : null}
            {watch("type") === "TOMRA_ITEM_TYPE" ||
            watch("type") === "TOMRA_ITEM_TYPE_COUNT" ||
            watch("type") === "TOMRA_ITEM_TYPE_VALUE" ? (
              <ControlledSelect
                disabled
                name="tomraItemType"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.tomraItemType)}
                label="Statiegeld product type"
                options={statiegeldProducts?.map((price) => ({
                  _id: price?._id as string,
                  title: price?.name as string,
                }))}
                placeholder="Pick Statiegeld item type here"
              />
            ) : null}
            {watch("type") === "STREAKS" ? (
              <ControlledSelect
                disabled
                name="streakType"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.streakType)}
                label="Streak type"
                options={streakTypes?.map((price) => ({
                  _id: price?._id as string,
                  title: price?.title as string,
                }))}
                placeholder="Pick Streak type here"
              />
            ) : null}
            {watch("type") === "STREAKS" ? (
              <ControlledInput
                disabled
                name="streakCount"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.streakCount)}
                label="Streak count"
                type="number"
                placeholder="Enter streak count here"
              />
            ) : null}
            {watch("type") === "DONATION" ? (
              <ControlledInput
                disabled
                name="donationCount"
                rules={{ required: false }}
                control={control}
                error={Boolean(errors?.donationCount)}
                label="Donation count"
                type="number"
                placeholder="Enter donation count here"
              />
            ) : null}
            {watch("type") === "DONATION" ? (
              // Read-only once the challenge repeats, like the scoring fields
              // above; a one-time challenge can still gain or drop its minimum.
              <ControlledInput
                disabled={isRepeating}
                name="minDonationAmount"
                rules={{ required: false, min: 0 }}
                control={control}
                error={Boolean(errors?.minDonationAmount)}
                label={
                  isRepeating
                    ? "Min donation amount in €"
                    : "Min donation amount in € (optional - leave empty to count any amount)"
                }
                type="number"
                placeholder="Enter min donation amount here"
              />
            ) : null}
            {watch("type") === "PARCEL_COUNT" ? (
              <>
                <p className="font-satoshi text-dark text-sm font-medium mb-3">
                  Carriers (a handoff with any one of these counts)
                </p>
                <div className="mb-4 flex flex-wrap gap-2">
                  {selectedCarrierNames.length ? (
                    selectedCarrierNames.map((name) => (
                      <span
                        key={name}
                        className="rounded-full bg-sky-lighter px-3 py-1 font-satoshi text-sm text-dark"
                      >
                        {name}
                      </span>
                    ))
                  ) : (
                    <span className="font-satoshi text-sm text-red-600">
                      No carriers on this challenge — it can never progress.
                    </span>
                  )}
                </div>
                <ControlledSelect
                  disabled
                  name="parcelDirection"
                  control={control}
                  error={Boolean(errors?.parcelDirection)}
                  label="Parcel direction"
                  options={parcelDirections}
                  placeholder="Pick which parcels count"
                />
                <ControlledInput
                  disabled
                  name="minParcelCount"
                  control={control}
                  error={Boolean(errors?.minParcelCount)}
                  label="Minimum parcel count"
                  type="number"
                  placeholder="Enter the number of parcels to reach"
                />
              </>
            ) : null}
            {isRepeatableType ? (
              <>
                {/* Read-only, like every other scoring field here. The
                    backend refuses these edits once anyone has progress:
                    which cycle a customer is on is their count divided by the
                    target, so moving it would rewrite how many rewards they
                    have already earned. */}
                <p className="font-satoshi text-dark text-sm font-medium mb-2">
                  Repeated completions
                </p>
                <p className="font-satoshi text-sm text-dark mb-4">
                  {isParcelChallenge
                    ? isRepeating
                      ? "On — a customer can finish this more than once, and every completion issues its own voucher."
                      : "Off — each customer can complete this challenge once."
                    : isRepeating
                      ? "On - a customer can finish this more than once, and every completion pays the reward again."
                      : "Off - each customer can complete this challenge once."}
                </p>
                {/* Parcel and repeating challenges only: a one-time challenge
                    of another type keeps the rules it always had. */}
                {isParcelChallenge || isRepeating ? (
                  <p className="font-satoshi text-xs text-gray-600 mb-4">
                    Conditions are fixed once the challenge has participants.
                    Stop it and copy it as a new challenge to change them.
                  </p>
                ) : null}
              </>
            ) : null}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Where it runs
              </label>
              <select
                value={targetType}
                onChange={(e) =>
                  setTargetType(e.target.value as "cities" | "locations")
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cities">Cities (every store in them)</option>
                <option value="locations">Specific stores</option>
              </select>
              {targetType === "locations" ? (
                <p className="mt-1 text-xs text-gray-500">
                  Only customers whose Droppie location is one of these stores
                  see this challenge, and only drops and parcels at these
                  stores count. Cities are ignored.
                </p>
              ) : null}
            </div>
            {targetType === "cities" ? (
              <DynamicFormInput
                generateIds={false}
                addOnSelect
                label="Cities"
                inputs={cities}
                setInputs={setCities}
                options={citiesToSelect ?? []}
              />
            ) : (
              <DynamicFormInput
                generateIds={false}
                addOnSelect
                label="Stores"
                inputs={stores}
                setInputs={setStores}
                options={storesToSelect}
              />
            )}
            <ControlledInput
              name="startsAt"
              rules={{ required: false }}
              control={control}
              error={Boolean(errors?.startsAt)}
              label="Start Date (optional — leave empty to go live immediately)"
              type="date"
              placeholder=""
            />
            <ControlledInput
              name="endsAt"
              rules={{ required: false }}
              control={control}
              error={Boolean(errors?.endsAt)}
              label="Expire Date"
              type="date"
              placeholder=""
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
      {onePartnerChallenge?.allowMultipleCompletions ? (
        <div className="w-full sm:mt-2 bg-white rounded-xl border-gray-border drop-shadow-none mb-2 sm:mb-0">
          <div className="w-full flex flex-row items-center justify-between border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Completions{" "}
              <span className="text-gray-500">
                ({challengeCompletionsTotal})
              </span>
            </p>
            <label className="flex flex-row items-center font-satoshi text-sm text-dark cursor-pointer">
              <input
                type="checkbox"
                className="mr-2"
                checked={pendingOnly}
                onChange={() => setPendingOnly(!pendingOnly)}
              />
              Waiting on stock only
            </label>
          </div>
          <div className="w-full px-7 py-7">
            {!challengeCompletions?.length ? (
              <p className="text-center font-satoshi text-base text-dark">
                {pendingOnly
                  ? "No completion is waiting on stock."
                  : "No completion recorded yet."}
              </p>
            ) : (
              <ChallengeCompletionsTable
                challengeType={onePartnerChallenge?.conditions?.type}
              />
            )}
            {hasNextChallengeCompletions ? (
              <div className="flex flex-row w-full items-center justify-end mt-3">
                <PrimaryButton
                  onClick={() => {
                    setCompletionsPage(completionsPage + 1);
                    getChallengeCompletions(
                      params?.id as string,
                      completionsPage + 1,
                      20,
                      pendingOnly ? "PENDING_STOCK" : undefined
                    );
                  }}
                  label="Load more"
                  className="h-10"
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {/* Left as it was for every non-recurring challenge, which still
          records its single completion on the progress document. */}
      <div className="w-full sm:mt-2 bg-white rounded-xl border-gray-border drop-shadow-none mb-2 sm:mb-0">
        <div className="w-full flex flex-row items-center justify-between border-b border-gray-border px-[28px] py-[15px]">
          <p className="font-satoshi font-medium text-base text-dark">
            Completed Challenges
          </p>
        </div>
        <div className="w-full px-7 py-7">
          {!allCompletedChallenges?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              There is no Challenge completed yet.
            </p>
          ) : (
            <CompletedChallengesTable />
          )}
          {hasNextCompletedChallenges ? (
            <div className="flex flex-row w-full items-center justify-end mt-3">
              <PrimaryButton
                onClick={() => {
                  setPage(page + 1);
                  getAllCompletedChallenges(page + 1, 10, params?.id as string);
                }}
                label="Load more"
                className="h-10"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(EditPartnerChallengeScreen);

const types = [
  { _id: "FIRST_DROP", title: "First Drop" },
  { _id: "DROP_COUNT", title: "Drop Count" },
  { _id: "DROP_VALUE", title: "Drop Value" },
  { _id: "ITEM_TYPE", title: "Item Type" },
  { _id: "ITEM_TYPE_COUNT", title: "Item Type Count" },
  { _id: "ITEM_TYPE_DROP_COUNT", title: "Item Type Drop Count" },
  { _id: "ITEM_TYPE_VALUE", title: "Item Type Value" },
  { _id: "TOMRA_ITEM_TYPE", title: "Statie Item Type" },
  { _id: "TOMRA_ITEM_TYPE_COUNT", title: "Statie Item Type Count" },
  { _id: "TOMRA_ITEM_TYPE_DROP_COUNT", title: "Statie Item Type Drop Count" },
  { _id: "TOMRA_ITEM_TYPE_VALUE", title: "Statie Item Type Value" },
  { _id: "DONATION", title: "Donation" },
  { _id: "INVITE", title: "Invite" },
  { _id: "STREAKS", title: "Streaks" },
  { _id: "POSTAL_CODE", title: "Postal code" },
  { _id: "COMPLETE_PROFILE", title: "Complete Profile" },
  { _id: "PARCEL_COUNT", title: "Parcel Count" },
];

const parcelDirections = [
  { _id: "EITHER", title: "Pickups and drop-offs (added together)" },
  { _id: "PICKUP", title: "Pickup only" },
  { _id: "DROPOFF", title: "Drop-off only" },
];

const streakTypes = [
  { _id: "DAILY", title: "Daily" },
  { _id: "WEEKLY", title: "Weekly" },
  { _id: "MONTHLY", title: "Monthly" },
];
